import { RoleScope } from '@features/team-management/types/customRoles';

/** Entity that may expose role_scope (preferred) or legacy role. */
export type RoleScopeSource = {
  role_scope?: RoleScope | string | null;
  role?: string | null;
};

/** Prefer role_scope; fall back to role during API rollout. */
export const resolveRoleScope = (
  source?: RoleScopeSource | null
): RoleScope | undefined => {
  if (!source) return undefined;
  const value = source.role_scope ?? source.role;
  return value ? (value as RoleScope) : undefined;
};

export const hasRoleScope = (
  source: RoleScopeSource | null | undefined,
  scope: RoleScope | string
): boolean => resolveRoleScope(source) === scope;

export const hasAnyRoleScope = (
  source: RoleScopeSource | null | undefined,
  scopes: readonly (RoleScope | string)[]
): boolean => {
  const roleScope = resolveRoleScope(source);
  return !!roleScope && scopes.includes(roleScope);
};

export const isSalesAgent = (
  source: RoleScopeSource | null | undefined
): boolean => hasRoleScope(source, RoleScope.SALES_AGENT);

export const isPaymentManager = (
  source: RoleScopeSource | null | undefined
): boolean => hasRoleScope(source, RoleScope.PAYMENT_MANAGER);

export const isAttendanceManager = (
  source: RoleScopeSource | null | undefined
): boolean => hasRoleScope(source, RoleScope.ATTENDANCE_MANAGER);

export const isGroupAdmin = (
  source: RoleScopeSource | null | undefined
): boolean => hasRoleScope(source, RoleScope.GROUP_ADMIN);

/** Owner, organisation admin, or individual account. */
export const OWNER_OR_ORG_ADMIN_SCOPES: readonly RoleScope[] = [
  RoleScope.OWNER,
  RoleScope.ORGANISATION_ADMIN,
  RoleScope.INDIVIDUAL
];

export const isOwnerOrOrgAdmin = (
  source: RoleScopeSource | null | undefined
): boolean => hasAnyRoleScope(source, OWNER_OR_ORG_ADMIN_SCOPES);

/** Can delete approved expenses (org admin, finance, or owner). */
export const APPROVED_EXPENSE_DELETE_SCOPES: readonly RoleScope[] = [
  RoleScope.ORGANISATION_ADMIN,
  RoleScope.FINANCE,
  RoleScope.OWNER
];

export const canDeleteApprovedExpense = (
  source: RoleScopeSource | null | undefined
): boolean => hasAnyRoleScope(source, APPROVED_EXPENSE_DELETE_SCOPES);

/** Owner or individual — e.g. hide "Create account" when user already owns an org. */
export const OWNER_OR_INDIVIDUAL_SCOPES: readonly RoleScope[] = [
  RoleScope.OWNER,
  RoleScope.INDIVIDUAL
];

/** Scopes allowed to share cofee intro message. */
export const INTRO_MESSAGE_SHARE_SCOPES: readonly RoleScope[] = [
  RoleScope.OWNER,
  RoleScope.ORGANISATION_ADMIN,
  RoleScope.BRANCH_ADMIN,
  RoleScope.INDIVIDUAL
];

/** Role scopes that land on / use lead-management UX. */
export const LEAD_MANAGEMENT_ROLES: readonly RoleScope[] = [
  RoleScope.SALES_AGENT,
  RoleScope.SALES_MANAGER
];

/** Role scopes for event manager / volunteer UX. */
export const EVENT_MANAGER_ROLES: readonly RoleScope[] = [
  RoleScope.EVENT_MANAGER,
  RoleScope.EVENT_VOLUNTEER
];
